import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  ALERT_HISTORY_VIEW,
  ALERTS_CREATE,
  ALERTS_UPDATE,
  ALERTS_VIEW,
} from "../../alertsPermissions";
import type { AlertsRouteFactory } from "./alertsRouteContext";
import { alertHistorySearchSchema, alertsSearchSchema } from "./searchSchemas";

const AlertsListScreen = lazyRouteComponent(
  () => import("../AlertsListScreen"),
  "AlertsListScreen",
);
const AlertDetailScreen = lazyRouteComponent(
  () => import("../AlertDetailScreen"),
  "AlertDetailScreen",
);
const AlertCreateScreen = lazyRouteComponent(
  () => import("../AlertCreateScreen"),
  "AlertCreateScreen",
);
const AlertEditScreen = lazyRouteComponent(
  () => import("../AlertEditScreen"),
  "AlertEditScreen",
);
const AlertHistoryScreen = lazyRouteComponent(
  () => import("../AlertHistoryScreen"),
  "AlertHistoryScreen",
);
const AlertHistoryDetailScreen = lazyRouteComponent(
  () => import("../AlertHistoryDetailScreen"),
  "AlertHistoryDetailScreen",
);

export const createAlertsListRoutes: AlertsRouteFactory = ({
  context,
  alerts,
  history,
  companies,
  branches,
  assets,
  assetTypes,
  devices,
  sensors,
}) => {
  const historyPath = `${context.basePath}/log`;

  const historyDetailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${historyPath}/$historyId`,
    beforeLoad: requirePermission([ALERT_HISTORY_VIEW]),
    component: function AlertHistoryDetailRouteScreen() {
      const { historyId } = historyDetailRoute.useParams() as unknown as {
        historyId: string;
      };
      return createElement(AlertHistoryDetailScreen, {
        repository: history,
        basePath: historyPath,
        historyId,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id`,
    beforeLoad: requirePermission([ALERTS_VIEW]),
    component: function AlertDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(AlertDetailScreen, {
        repository: alerts,
        basePath: context.basePath,
        alertId: id,
        assetTypes,
      });
    },
  });

  const pickers = { companies, branches, assets, assetTypes, devices };

  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id/edit`,
    beforeLoad: requirePermission([ALERTS_UPDATE]),
    component: function AlertEditRouteScreen() {
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(AlertEditScreen, {
        repository: alerts,
        basePath: context.basePath,
        alertId: id,
        ...pickers,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: context.basePath,
      validateSearch: alertsSearchSchema,
      beforeLoad: requirePermission([ALERTS_VIEW]),
      component: () =>
        createElement(AlertsListScreen, {
          repository: alerts,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/create`,
      beforeLoad: requirePermission([ALERTS_CREATE]),
      component: () =>
        createElement(AlertCreateScreen, {
          repository: alerts,
          basePath: context.basePath,
          ...pickers,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: historyPath,
      validateSearch: alertHistorySearchSchema,
      beforeLoad: requirePermission([ALERT_HISTORY_VIEW]),
      component: () =>
        createElement(AlertHistoryScreen, {
          repository: history,
          basePath: historyPath,
          companies,
          assets,
          sensors,
        }),
    }),
    historyDetailRoute,
    editRoute,
    // Bare '$id' is the most general path here -- kept last so '/create'
    // and '/log' are never captured as an alert id.
    detailRoute,
  ];
};
